import React, { useState } from 'react';
import {
  Box,
  Button,
  Divider,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  BookmarkBorder as SavedViewIcon,
  DeleteOutline as DeleteIcon,
} from '@mui/icons-material';

export const STAGED_SAVED_VIEWS_KEY = 'stagedProducts.savedViews';

export interface StagedSavedView {
  id: string;
  name: string;
  /** Serialised search params — tab, search text and every applied filter. */
  query: string;
}

/** Saved views are per-browser; the query string is the whole view. */
export function readStagedSavedViews(): StagedSavedView[] {
  try {
    const raw = localStorage.getItem(STAGED_SAVED_VIEWS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (v): v is StagedSavedView =>
        !!v && typeof v.id === 'string' && typeof v.name === 'string' && typeof v.query === 'string',
    );
  } catch {
    return [];
  }
}

export function writeStagedSavedViews(views: StagedSavedView[]): void {
  try {
    localStorage.setItem(STAGED_SAVED_VIEWS_KEY, JSON.stringify(views));
  } catch {
    /* Private-mode / quota — saved views are a convenience, not state we own. */
  }
}

export interface SavedViewsButtonProps {
  /** The current query string, saved verbatim. */
  currentQuery: string;
  onApply: (query: string) => void;
}

export default function SavedViewsButton({ currentQuery, onApply }: SavedViewsButtonProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [views, setViews] = useState<StagedSavedView[]>(() => readStagedSavedViews());
  const [name, setName] = useState('');

  const close = () => {
    setAnchorEl(null);
    setName('');
  };

  const persist = (next: StagedSavedView[]) => {
    setViews(next);
    writeStagedSavedViews(next);
  };

  const save = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const view: StagedSavedView = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      name: trimmed,
      query: currentQuery,
    };
    persist([...views.filter((v) => v.name !== trimmed), view]);
    close();
  };

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        color="inherit"
        startIcon={<SavedViewIcon fontSize="small" />}
        onClick={(e) => {
          setViews(readStagedSavedViews());
          setAnchorEl(e.currentTarget);
        }}
        sx={{
          height: 30,
          fontSize: '0.78125rem',
          fontWeight: 500,
          color: 'text.secondary',
          borderColor: 'divider',
        }}
      >
        Save as view
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 300, mt: 0.75 } } }}
      >
        <Box sx={{ px: 1.5, py: 1 }}>
          <Stack direction="row" spacing={1}>
            <TextField
              size="small"
              fullWidth
              autoFocus
              value={name}
              placeholder="Name this view"
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  save();
                  return;
                }
                // Escape and Tab belong to the Menu (close / move on); everything
                // else stays here so typing a name does not drive MenuList type-ahead.
                if (e.key !== 'Escape' && e.key !== 'Tab') e.stopPropagation();
              }}
              inputProps={{ 'aria-label': 'Saved view name' }}
            />
            <Button size="small" variant="contained" disabled={!name.trim()} onClick={save}>
              Save
            </Button>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
            Saves the current tab, search and filters on this browser.
          </Typography>
        </Box>
        {views.length > 0 && <Divider />}
        {views.map((view) => (
          <MenuItem
            key={view.id}
            onClick={() => {
              onApply(view.query);
              close();
            }}
            sx={{ pr: 1 }}
          >
            <ListItemText
              primary={view.name}
              slotProps={{ primary: { fontSize: '0.8125rem', noWrap: true } }}
            />
            <Tooltip title="Delete view">
              <IconButton
                size="small"
                edge="end"
                aria-label={`Delete saved view ${view.name}`}
                onClick={(e) => {
                  e.stopPropagation();
                  persist(views.filter((v) => v.id !== view.id));
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
